import { Route } from '@angular/router';
import { initialDataResolver } from 'app/app.resolvers';
import { AuthGuard } from 'app/core/auth/guards/auth.guard';
import { ManagerAdminGuard } from 'app/core/auth/guards/managerAdmin.guard';
import { NoAuthGuard } from 'app/core/auth/guards/noAuth.guard';
import { RoleGuard } from 'app/core/auth/guards/role.guard';
import { LayoutComponent } from 'app/layout/layout.component';

import { PartTimerComponent } from './modules/register/part-timer/part-timer.component';

// @formatter:off
/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/explicit-function-return-type */
export const appRoutes: Route[] = [
    // Redirect empty path to '/example'
    { path: '', pathMatch: 'full', redirectTo: 'project-management' },

    // Redirect signed-in user to the '/example'
    //
    // After the user signs in, the sign-in page will redirect the user to the 'signed-in-redirect'
    // path. Below is another redirection for that path to redirect the user to the desired
    // location. This is a small convenience to keep all main routes together here on this file.
    {
        path: 'signed-in-redirect',
        pathMatch: 'full',
        redirectTo: 'project-management',
    },

    // Auth routes for guests
    {
        path: '',
        canActivate: [NoAuthGuard],
        canActivateChild: [NoAuthGuard],
        component: LayoutComponent,
        data: {
            layout: 'empty',
        },
        children: [
            {
                path: 'confirmation-required',
                loadChildren: () =>
                    import(
                        'app/modules/auth/confirmation-required/confirmation-required.routes'
                    ),
            },
            {
                path: 'forgot-password',
                loadChildren: () =>
                    import(
                        'app/modules/auth/forgot-password/forgot-password.routes'
                    ),
            },
            {
                path: 'reset-password',
                loadChildren: () =>
                    import(
                        'app/modules/auth/reset-password/reset-password.routes'
                    ),
            },
            {
                path: 'sign-in',
                loadChildren: () =>
                    import('app/modules/auth/sign-in/sign-in.routes'),
            },
            {
                path: 'sign-up',
                loadChildren: () =>
                    import('app/modules/auth/sign-up/sign-up.routes'),
            },
        ],
    },

    // Auth routes for authenticated users
    {
        path: '',
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        data: {
            layout: 'empty',
        },
        children: [
            {
                path: 'sign-out',
                loadChildren: () =>
                    import('app/modules/auth/sign-out/sign-out.routes'),
            },
            {
                path: 'unlock-session',
                loadChildren: () =>
                    import(
                        'app/modules/auth/unlock-session/unlock-session.routes'
                    ),
            },
        ],
    },

    // Landing routes
    {
        path: '',
        component: LayoutComponent,
        data: {
            layout: 'empty',
        },
        children: [
            {
                path: 'home',
                loadChildren: () =>
                    import('app/modules/landing/home/home.routes'),
            },
        ],
    },

    // Admin routes
    {
        path: '',
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: initialDataResolver,
        },
        children: [
            {
                path: 'project-management',
                canActivate: [RoleGuard('admin', 'manager')],
                loadChildren: () =>
                    import(
                        'app/modules/admin/project-management/project-management.routes'
                    ),
            },
            {
                path: 'roster-management',
                canActivate: [RoleGuard('admin', 'manager')],
                loadChildren: () =>
                    import(
                        'app/modules/admin/roster-management/roster-management.routes'
                    ),
            },
            {
                path: 'project-location-management',
                canActivate: [RoleGuard('admin', 'manager')],
                loadChildren: () =>
                    import(
                        'app/modules/admin/project-location-management/project-location-management.routes'
                    ),
            },
            {
                path: 'client-management',
                canActivate: [RoleGuard('admin', 'manager')],
                loadChildren: () =>
                    import(
                        'app/modules/admin/client-management/client-management.routes'
                    ),
            },
            {
                path: 'part-timer-management',
                canActivate: [RoleGuard('admin', 'manager', 'hr')],
                loadChildren: () =>
                    import(
                        'app/modules/admin/part-timer-management/part-timer-management.routes'
                    ),
            },
            {
                path: 'contractor-management',
                canActivate: [RoleGuard('admin', 'manager', 'hr')],
                loadChildren: () =>
                    import(
                        'app/modules/admin/contractor-management/contractor-management.routes'
                    ),
            },
            {
                path: 'certificate-management',
                canActivate: [RoleGuard('admin', 'manager', 'hr')],
                loadChildren: () =>
                    import(
                        'app/modules/admin/certificate-management/certificate-management.routes'
                    ),
            },
            {
                path: 'employee-management',
                canActivate: [RoleGuard('admin', 'manager', 'hr')],
                loadChildren: () =>
                    import(
                        'app/modules/admin/employee-management/employee-management.routes'
                    ),
            },
            {
                path: 'employee-pool',
                canActivate: [RoleGuard('admin', 'manager', 'hr', 'finance')],
                loadChildren: () =>
                    import(
                        'app/modules/admin/employee-pool/employee-pool.routes'
                    ),
            },
            {
                path: 'report-management',
                canActivate: [RoleGuard('admin', 'manager')],
                loadChildren: () =>
                    import(
                        'app/modules/admin/report-management/report-management.routes'
                    ),
            },
            {
                path: 'finance-management',
                canActivate: [RoleGuard('admin', 'finance')],
                loadChildren: () =>
                    import(
                        'app/modules/admin/finance-management/finance-management.routes'
                    ),
            },
            {
                path: 'admin-management',
                canActivate: [RoleGuard('admin')],
                loadChildren: () =>
                    import(
                        'app/modules/admin/admin-management/admin-management.routes'
                    ),
            },
            {
                path: 'data-migration',
                canActivate: [RoleGuard('admin', 'manager')],
                loadChildren: () =>
                    import(
                        'app/modules/admin/data-migration/data-migration.routes'
                    ),
            },
            {
                path: 'settings',
                canActivate: [RoleGuard('admin')],
                loadChildren: () =>
                    import('app/modules/admin/settings/settings.routes'),
            },
            {
                path: 'claim-management',
                canActivate: [RoleGuard('admin', 'finance')],
                loadChildren: () =>
                    import(
                        'app/modules/admin/claim-management/claim-management.routes'
                    ),
            },
        ],
    },

    {
        path: 'register/part-timer',
        component: PartTimerComponent,
        canActivate: [ManagerAdminGuard],
    },
    {
        path: 'contractor-service-agreement',
        loadChildren: () =>
            import(
                './modules/register/contractor-service-agreement/contractor-service-agreement.module'
            ).then((m) => m.ContractorServiceAgreementModule),
    },
];
